<template>
  <VRow
    v-if="showNightCardSection"
    class="d-flex"
    :class="{'flex-column': $vuetify.breakpoint.smAndDown, 'pl-6': $vuetify.breakpoint.smAndUp }"
  >
    <VCol
      lg="3"
      md="3"
      sm="12"
      xs="12"
      class="d-flex align-items-center"
      :class="{'flex-column': $vuetify.breakpoint.mdAndUp}"
    >
      <div :class="{'w-100': $vuetify.breakpoint.mdAndUp}">
        <VIcon
          large
          class="checkout-icon"
          :class="{'pr-1': $vuetify.breakpoint.smAndDown }"
        >
          mdi-ticket
        </VIcon>
      </div>
      <div :class="{'w-100': $vuetify.breakpoint.mdAndUp}">
        <span class="body-bold">{{ $t('nightRider.title') }}</span>
      </div>
    </VCol>
    <VCol
      cols="12"
      md="8"
      lg="8"
      sm="11"
    >
      <template v-if="checkoutNoLinkedCardBlock">
        <div class="d-flex"
          :class="{
            'flex-column': $vuetify.breakpoint.smAndDown,
            'align-items-start': $vuetify.breakpoint.smAndDown,
            'justify-space-between': $vuetify.breakpoint.mdAndUp,
            'align-items-center': $vuetify.breakpoint.mdAndUp,
          }">
          <div class="left-block">
            <span class="body-1">{{ $t('nightRiderLink.checkoutNoCardTitle') }}</span>
          </div>
          <div class="right-block">
            <VBtn
              class="w-100"
              color="brandingPrimary"
              text
              data-test="checkout-add-night-card-link"
              @click="addCardDialogOpen = true"
            >
              <span class="button">{{ $t('nightRiderLink.settingsAddCard') }}</span>
            </VBtn>
          </div>
        </div>
        <VDivider class="mt-1" />
        <div v-if="infoLinkUrl" class="pb-2 d-flex align-items-center">
          <VIcon class="secondary--text">
            mdi-help-circle-outline
          </VIcon>
          <a
            :href="infoLinkUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="body-2 secondary--text text-decoration-underline ml-2"
          >
            {{ $t('nightRiderLink.inviteLinkText') }}
          </a>
        </div>
        <VDialog v-model="addCardDialogOpen" max-width="600" scrollable>
          <VCard>
            <VCardTitle class="heading-6 pb-2 pt-4 px-4">
              {{ $t('nightRiderLink.checkoutAddCardDialogTitle') }}
            </VCardTitle>
            <VCardText class="pa-4 pt-0">
              <SNightRiderCardLinkForm
                ref="checkoutLinkForm"
                :show-primary-action="false"
                :account-phone="accountPhoneForLink"
                :info-link-url="infoLinkUrl"
                @submit="onSubmitCheckoutAddCard"
              />
            </VCardText>
            <div
              class="pa-4 pt-0 d-flex flex-row flex-nowrap align-stretch night-card-add-dialog-actions"
              :class="{'flex-column': $vuetify.breakpoint.smAndDown}"
              style="width: 100%; column-gap: 8px;"
            >
              <div class="night-card-add-dialog-actions__cell">
                <VBtn
                  outlined
                  class="w-100"
                  data-test="checkout-add-card-cancel"
                  @click="onCheckoutAddCardCancel"
                >
                  <span class="button">{{ $t('nightRiderLink.inviteLater') }}</span>
                </VBtn>
              </div>
              <div class="night-card-add-dialog-actions__cell" :class="{'mt-2': $vuetify.breakpoint.smAndDown}">
                <VBtn
                  color="brandingPrimary"
                  dark
                  class="w-100 text-decoration-none"
                  data-test="checkout-add-card-save"
                  @click="onCheckoutAddCardSaveClick"
                >
                  <span class="button">{{ $t('nightRiderLink.settingsAddCard') }}</span>
                </VBtn>
              </div>
            </div>
          </VCard>
        </VDialog>
      </template>
      <template v-else-if="showNightCardCheckoutContent">
        <SInfoblock
          v-if="getIsSomeRideIsOnThursday && !getIsAllRideIsOn31DecemberAnd1January"
          color="warning-color"
          class="mb-1"
          :class="{'mb-3': getIsSomeRideIsOnThursday && !getIsAllRideIsOn31DecemberAnd1January}"
          :no-margin="true"
          data-test="checkoutNightRiderRideOnThursdayDisclaimer"
          :message="$t('nightRider.nightCardSupportOnThursday')"
        />
        <SInfoblock
          v-if="showOutOfCommuneDisclaimer"
          color="blue-color"
          class="mb-3"
          :no-margin="true"
          data-test="checkoutNightRiderOutOfCommuneDisclaimer"
          :message="$t('nightRider.disclaimerOutOfCommune')"
        />
        <SInfoblock
          v-if="mainPassengerDisclaimerMessage"
          color="blue-color"
          class="mb-3"
          :no-margin="true"
          data-test="checkoutNightRiderValidationErrorDisclaimer"
          :message="mainPassengerDisclaimerMessage"
        />
        <template v-if="!getIsAllRidesIsOnThursday || getIsSomeRideIsOn31DecemberAnd1January">
          <ValidationObserver
            ref="nightCards"
            name="nightCards"
          >
            <div v-if="showLinkedAccountCardField" class="pb-3">
              <VTextField
                :value="linkedNightRiderCardNumber"
                readonly
                disabled
                data-test="linked-account-night-card"
                :label="$t('nightRider.nightCardLabel', { number: 1 })"
                :class="linkedCardFieldError ? 'pb-6' : ''"
                :error="!!linkedCardFieldError"
                :error-messages="linkedCardFieldError"
              />
            </div>
            <div v-if="showPassengerCardFields">
              <div v-if="showSectionTitle" class="pb-3">
                <span
                  class="body-2"
                  v-html="$t('nightRider.sectionTitle')"
                />
              </div>
              <ValidationProvider
                v-for="(card, index) in cards"
                v-slot="{ errors, valid }"
                :key="card.id"
                ref="card"
                name="nightCard"
                :rules="validationRulesForCard(card)"
                :allowFalse="false"
                mode="aggressive"
              >
                <VTextField
                  v-model="card.value"
                  clear-icon="mdi-close-circle"
                  clearable
                  :class="(!!errors.length || card.isBackendValid === false) && !!card.value ? 'pb-6' : ''"
                  :label="$t('nightRider.nightCardLabel', { number: getPassengerCardLabelNumber(index) })"
                  :error="!!errors.length || card.isBackendValid === false"
                  :error-messages="getCardErrorMessage(card, errors[0])"
                  @input="onCardInput(card, valid)"
                  @blur="onCardBlur(card, valid)"
                />
              </ValidationProvider>
            </div>
          </ValidationObserver>
        </template>
      </template>
    </VCol>
  </VRow>
</template>

<script lang="ts">
export default {
  name: 'NightCards',
};
</script>

<script setup lang="ts">
import Vue, {
  computed, nextTick, reactive, ref, watch,
} from 'vue';
import moment from 'moment-timezone';
import {
  SUserService,
  SNightRiderCardLinkForm,
} from '@slg/web-customer-shared';
import { emitNightCardLinkError } from '@slg/web-customer-shared/src/utils/nightCardLinkErrorEvents';
import { withNightCardNameFallback } from '@/utils/nightCardLinkHelpers';
import SInfoblock from '@slg/web-customer-shared/src/components/common/SInfoblock.vue';
import { bookingRequestNightCardsCheck } from '@/services/BookingRequestsService';
import {
  isNightCardDiscountEligible,
  isNightCardOutOfCommune,
  isNightCardFieldInvalid,
  isMainPassengerCardReadyForValidation,
  getNightRiderCardValidationEntries,
  parseNightCardValidationResult,
  resolveBackendValidFromValidationResult,
  resolveDisclaimerMessage,
  resolveFieldErrorMessage,
} from '@/utils/nightCardCheckoutLogic';
import {
  useStore, useI18n, useVm, useValidationError, useGlobalErrorDialog, useGtm,
} from '@/composables';

const props = defineProps({
  isAuth: {
    type: Boolean,
    default: false,
  },
  isAnotherPassenger: {
    type: Boolean,
    default: false,
  },
  linkedNightRiderCardNumber: {
    type: String,
    default: null,
  },
  accountPhoneForLink: {
    type: String,
    default: '',
  },
  infoLinkUrl: {
    type: String,
    default: '',
  },
  mainPassengerNightCard: {
    type: Object,
    default: null,
  },
  validateMainPassengerNightCard: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['updateNightCards', 'update:mainPassengerNightCard', 'night-card-linked']);
const store = useStore();
const { t, i18n } = useI18n();
const vm = useVm();
const { validationErrorResolve } = useValidationError();
const { setGlobalError } = useGlobalErrorDialog();
const { trackGtmEvent } = useGtm();

const cards = ref<any[]>([]);
const addCardDialogOpen = ref(false);
const cardValidationResults = reactive<Record<string, any>>({});
const linkedCardValidationResult = ref<any>(null);

const getAdults = computed(() => store.getters['booking/getAdults']);
const getChildren = computed(() => store.getters['booking/getChildren']);
const getBookingRequestId = computed(() => store.getters['booking/getBookingRequestId']);
const getPassengerPhone = computed(() => store.getters['booking/getPassengerPhone']);
const getAppConfig = computed(() => store.getters['common/getAppConfig']);
const getSelectedRides = computed(() => store.getters['booking/getSelectedRides']);
const getIsNightCardSupport = computed(() => getAppConfig.value?.isNightCardsSupport);
const effectiveLinkedCardNumber = computed(() => {
  if (!props.linkedNightRiderCardNumber) {
    return '';
  }
  return String(props.linkedNightRiderCardNumber);
});

function getValidationResultForCard(cardNumber: string) {
  if (!cardNumber) {
    return null;
  }
  const normalizedCardNumber = String(cardNumber).trim().toUpperCase();
  return cardValidationResults[normalizedCardNumber]
    || cardValidationResults[cardNumber]
    || null;
}

const mainPassengerCardNumber = computed(() => {
  if (props.isAnotherPassenger) {
    return (props.mainPassengerNightCard?.value || '').trim();
  }
  return effectiveLinkedCardNumber.value || '';
});

const mainPassengerValidationResult = computed(() => {
  if (props.isAnotherPassenger) {
    return props.mainPassengerNightCard?.backendValidationResult
      || getValidationResultForCard(mainPassengerCardNumber.value);
  }
  return linkedCardValidationResult.value
    || getValidationResultForCard(effectiveLinkedCardNumber.value);
});

const checkoutNoLinkedCardBlock = computed(() => getIsNightCardSupport.value
  && props.isAuth
  && !props.isAnotherPassenger
  && !props.linkedNightRiderCardNumber);

const isMainPassengerDiscountEligible = computed(() => {
  if (props.isAnotherPassenger && !isMainPassengerCardReadyForValidation(mainPassengerCardNumber.value)) {
    return false;
  }
  return isNightCardDiscountEligible(mainPassengerValidationResult.value);
});

function getCardSlotsCount() {
  if (!isMainPassengerDiscountEligible.value) {
    if (props.isAnotherPassenger || showLinkedAccountCardField.value) {
      return 0;
    }
  }

  if (props.isAnotherPassenger || showLinkedAccountCardField.value) {
    return Math.max(0, getAdults.value - 1);
  }

  return getAdults.value;
}

const showLinkedAccountCardField = computed(() => !props.isAnotherPassenger && !!props.linkedNightRiderCardNumber);
const showPassengerCardFields = computed(() => isMainPassengerDiscountEligible.value
  && getAdults.value > 1
  && getCardSlotsCount() > 0);

const getIsSomeRideIsOn31DecemberAnd1January = computed(() => getSelectedRides.value.some((ride: any) => {
  const outwardRideTime = ride.isDepartureExactTime ? ride.dateState.time : ride.dateState.time - ride.timeInMinutes;
  return (moment(outwardRideTime).tz(ride.timeZone).month() === 11 && moment(outwardRideTime).tz(ride.timeZone).date() === 31)
    || (moment(outwardRideTime).tz(ride.timeZone).month() === 0 && moment(outwardRideTime).tz(ride.timeZone).date() === 1);
}));
const getIsAllRideIsOn31DecemberAnd1January = computed(() => getSelectedRides.value.every((ride: any) => {
  const outwardRideTime = ride.isDepartureExactTime ? ride.dateState.time : ride.dateState.time - ride.timeInMinutes;
  return (moment(outwardRideTime).tz(ride.timeZone).month() === 11 && moment(outwardRideTime).tz(ride.timeZone).date() === 31)
    || (moment(outwardRideTime).tz(ride.timeZone).month() === 0 && moment(outwardRideTime).tz(ride.timeZone).date() === 1);
}));
const getIsSomeRideIsOnThursday = computed(() => getSelectedRides.value.some((ride: any) => {
  const outwardRideTime = ride.isDepartureExactTime ? ride.dateState.time : ride.dateState.time - ride.timeInMinutes;
  return moment(outwardRideTime).tz(ride.timeZone).day() === 4;
}));
const getIsAllRidesIsOnThursday = computed(() => getSelectedRides.value.every((ride: any) => {
  const outwardRideTime = ride.isDepartureExactTime ? ride.dateState.time : ride.dateState.time - ride.timeInMinutes;
  return moment(outwardRideTime).tz(ride.timeZone).day() === 4;
}));

const isAnotherPassengerNightCardSectionVisible = computed(() => {
  if (!isMainPassengerDiscountEligible.value) {
    return false;
  }
  const hasThursdayNotice = getIsSomeRideIsOnThursday.value
    && !getIsAllRideIsOn31DecemberAnd1January.value;
  return showPassengerCardFields.value || hasThursdayNotice;
});

const showNightCardSection = computed(() => {
  if (!getIsNightCardSupport.value || !props.isAuth) {
    return false;
  }
  if (props.isAnotherPassenger) {
    return isAnotherPassengerNightCardSectionVisible.value;
  }
  return true;
});

const showNightCardCheckoutContent = computed(() => {
  if (!getIsNightCardSupport.value || !props.isAuth) {
    return false;
  }
  if (props.isAnotherPassenger) {
    return isAnotherPassengerNightCardSectionVisible.value;
  }
  return !!props.linkedNightRiderCardNumber;
});

const isMainPassengerOutOfCommune = computed(() => isNightCardOutOfCommune(mainPassengerValidationResult.value));
const showOutOfCommuneDisclaimer = computed(() => {
  if (props.isAnotherPassenger) {
    return false;
  }
  return isMainPassengerOutOfCommune.value && !!props.linkedNightRiderCardNumber;
});
const mainPassengerDisclaimerMessage = computed(() => {
  if (props.isAnotherPassenger || !props.linkedNightRiderCardNumber) {
    return '';
  }
  return resolveDisclaimerMessage(
    mainPassengerValidationResult.value,
    t as any,
    i18n.locale,
  );
});
const linkedCardFieldError = computed(() => {
  if (!showLinkedAccountCardField.value || !isNightCardFieldInvalid(mainPassengerValidationResult.value)) {
    return '';
  }
  return resolveFieldErrorMessage(mainPassengerValidationResult.value, t as any);
});
const showSectionTitle = computed(() => showPassengerCardFields.value);
const linkedCardLabelOffset = computed(() => (showLinkedAccountCardField.value ? 1 : 0));

function getPassengerCardLabelNumber(index: number) {
  if (props.isAnotherPassenger) {
    return index + 2;
  }
  return index + 1 + linkedCardLabelOffset.value;
}

function getAdditionalCardsForDuplicateCheck() {
  return cards.value.filter(({ value }) => (value || '').trim());
}

function getMainPassengerValidationResult() {
  return mainPassengerValidationResult.value;
}

function getCardsForDuplicateCheck() {
  if (props.isAnotherPassenger) {
    const cardsToCheck = getAdditionalCardsForDuplicateCheck();
    if (mainPassengerCardNumber.value) {
      cardsToCheck.unshift({
        value: mainPassengerCardNumber.value,
      });
    }
    return cardsToCheck;
  }

  const cardsToCheck = [...cards.value];
  if (showLinkedAccountCardField.value && props.linkedNightRiderCardNumber) {
    cardsToCheck.unshift({
      value: String(props.linkedNightRiderCardNumber).toUpperCase(),
    });
  }
  return cardsToCheck;
}

function validationRulesForCard(card: any) {
  if (!card.value) {
    return {};
  }
  return {
    min: 7,
    max: 10,
    onlyLettersAndNumbers: /^[a-zA-Z0-9]+$/,
    equalsCards: {
      cards: getCardsForDuplicateCheck(),
      cardValue: card.value,
    },
  };
}

function rebuildCardsFromAdults() {
  const prevByIndex = (cards.value || []).map((c) => ({ ...c }));
  const slotsCount = getCardSlotsCount();

  cards.value = [];

  for (let i = 0; i < slotsCount; i += 1) {
    const prev = prevByIndex[i];
    cards.value.push({
      id: prev?.id || `card-${i}`,
      value: prev?.value ?? null,
      isFrontValid: prev?.isFrontValid ?? false,
      isBackendValid: prev?.isBackendValid ?? null,
      backendValidationResult: prev?.backendValidationResult ?? null,
    });
  }
}

function restoreFromLocalStorage() {
  if (checkoutNoLinkedCardBlock.value || props.isAnotherPassenger) {
    return;
  }
  if (!localStorage.getItem('cards')) {
    return;
  }
  const cardsFromLocalStorage = JSON.parse(localStorage.getItem('cards') as string);
  const storageOffset = linkedCardLabelOffset.value;
  const neededCardsFromLocalStorage = getAdults.value - storageOffset >= cardsFromLocalStorage.length
    ? cardsFromLocalStorage
    : cardsFromLocalStorage.slice(0, getAdults.value - storageOffset);

  neededCardsFromLocalStorage.forEach(({
    id, value, isFrontValid, isBackendValid,
  }: any, index: number) => {
    const cardIndex = index + storageOffset;
    if (!cards.value[cardIndex]) {
      return;
    }
    cards.value[cardIndex].id = id;
    cards.value[cardIndex].value = value;
    cards.value[cardIndex].isFrontValid = isFrontValid;
    cards.value[cardIndex].isBackendValid = isBackendValid;
  });
}

function collectCardsForValidation() {
  const cardNumbers: string[] = [];

  if (!props.isAnotherPassenger && effectiveLinkedCardNumber.value) {
    cardNumbers.push(effectiveLinkedCardNumber.value);
  }

  if (props.isAnotherPassenger && isMainPassengerCardReadyForValidation(mainPassengerCardNumber.value)) {
    cardNumbers.push(mainPassengerCardNumber.value);
  }

  cards.value.forEach(({ value }) => {
    const trimmed = (value || '').trim();
    if (isMainPassengerCardReadyForValidation(trimmed) && !cardNumbers.includes(trimmed)) {
      cardNumbers.push(trimmed);
    }
  });
  return cardNumbers;
}

function getValidCardsForOrder() {
  return collectCardsForValidation().filter(
    (cardNumber) => isNightCardDiscountEligible(
      getValidationResultForCard(cardNumber),
    ),
  );
}

function pruneStaleCardValidationResults() {
  const activeCardNumbers = collectCardsForValidation().map(
    (cardNumber) => String(cardNumber).trim().toUpperCase(),
  );
  Object.keys(cardValidationResults).forEach((cardNumber) => {
    const normalizedCardNumber = String(cardNumber).trim().toUpperCase();
    if (!activeCardNumbers.includes(normalizedCardNumber)) {
      Vue.delete(cardValidationResults, cardNumber);
    }
  });
}

function shouldRunNightCardPriceCheck() {
  if (checkoutNoLinkedCardBlock.value) {
    return false;
  }
  return collectCardsForValidation().length > 0 || props.isAnotherPassenger;
}

function syncLinkedCardValidationResult() {
  if (props.isAnotherPassenger || !props.linkedNightRiderCardNumber) {
    return;
  }
  const validationResult = getValidationResultForCard(effectiveLinkedCardNumber.value);
  if (validationResult) {
    linkedCardValidationResult.value = validationResult;
  }
}

function applyCardValidationResult(cardNumber: string, validationResult: any) {
  if (!cardNumber) {
    return;
  }
  const parsedResult = parseNightCardValidationResult(validationResult);
  const normalizedCardNumber = String(cardNumber).trim().toUpperCase();
  Vue.set(cardValidationResults, normalizedCardNumber, parsedResult);

  if (props.isAnotherPassenger && props.mainPassengerNightCard
    && normalizedCardNumber === mainPassengerCardNumber.value.toUpperCase()) {
    emit('update:mainPassengerNightCard', {
      ...props.mainPassengerNightCard,
      backendValidationResult: parsedResult,
      isBackendValid: resolveBackendValidFromValidationResult(parsedResult),
    });
  }

  syncLinkedCardValidationResult();

  const cardIndex = cards.value.findIndex(
    ({ value }) => value && String(value).trim().toUpperCase() === normalizedCardNumber,
  );
  if (cardIndex >= 0) {
    const updatedCard = {
      ...cards.value[cardIndex],
      backendValidationResult: parsedResult,
      isBackendValid: resolveBackendValidFromValidationResult(parsedResult),
    };
    cards.value.splice(cardIndex, 1, updatedCard);
  }
}

function trackNightCardEntering(cardValue: string) {
  if (!cardValue || cardValue.length < 7) {
    return;
  }
  trackGtmEvent('Enter night card', 'Input', 'Type', 'Entering Night Card');
}

async function validateAdditionalCardFieldsForPriceCheck() {
  const cardRefs = (vm.$refs as any).card;
  if (!cardRefs) {
    return true;
  }
  const refs = Array.isArray(cardRefs) ? cardRefs : [cardRefs];
  const results = await Promise.all(
    refs.map((refItem: any) => (refItem?.validate ? refItem.validate() : Promise.resolve(true))),
  );
  return results.every(Boolean);
}

async function validateFormForPriceCheck() {
  return new Promise((resolve) => {
    nextTick(async () => {
      if (props.isAnotherPassenger && props.validateMainPassengerNightCard) {
        const cardNumber = (props.mainPassengerNightCard?.value || '').trim();
        if (cardNumber) {
          const isMainCardValid = await props.validateMainPassengerNightCard();
          if (!isMainCardValid) {
            resolve(false);
            return;
          }
        }
      }

      if (showPassengerCardFields.value) {
        const isFormValid = await validateAdditionalCardFieldsForPriceCheck();
        resolve(isFormValid);
        return;
      }

      resolve(true);
    });
  });
}

async function validateForm() {
  return validateFormForPriceCheck();
}

function onMainPassengerNightCardInput(value: string, valid: boolean) {
  const normalizedValue = value ? value.toUpperCase() : value;
  emit('update:mainPassengerNightCard', {
    ...(props.mainPassengerNightCard || {}),
    value: normalizedValue,
    isBackendValid: null,
    backendValidationResult: null,
    isFrontValid: valid,
  });

  if (!normalizedValue) {
    checkCards();
  }
}

function onMainPassengerNightCardBlur(valid: boolean) {
  const card = {
    ...(props.mainPassengerNightCard || {}),
    isFrontValid: valid,
  };
  emit('update:mainPassengerNightCard', card);

  if (!card.value) {
    checkCards();
    return;
  }
  if (!valid || !isMainPassengerCardReadyForValidation(card.value)) {
    emit('updateNightCards', getValidCardsForOrder());
    return;
  }
  checkCards();
}

function removeFromLocalStorage(card: any) {
  const cardsFromLocalStorage = JSON.parse(localStorage.getItem('cards') as string);
  if (!cardsFromLocalStorage) {
    return;
  }
  const cardsSavedInLocalStorage = cardsFromLocalStorage.filter(({ id }: any) => (id !== card.id));
  localStorage.setItem('cards', JSON.stringify(cardsSavedInLocalStorage));
}

function onCardInput(card: any, valid: boolean) {
  if (card.value) {
    card.value = card.value.toUpperCase();
  }
  removeFromLocalStorage(card);
  card.isBackendValid = null;
  card.backendValidationResult = null;
  card.isFrontValid = valid;

  if (props.isAnotherPassenger) {
    if (!card.value) {
      checkCards();
    }
    return;
  }

  checkCards();
  trackNightCardEntering(card.value);
}

function onCardBlur(card: any, valid: boolean) {
  card.isFrontValid = valid;
  if (!props.isAnotherPassenger) {
    return;
  }
  if (!card.value) {
    checkCards();
    return;
  }
  if (!valid || !isMainPassengerCardReadyForValidation(card.value)) {
    emit('updateNightCards', getValidCardsForOrder());
    return;
  }
  checkCards();
}

function getCardErrorMessage(card: any, frontError: any) {
  if (frontError) {
    return validationErrorResolve(frontError);
  }
  if (card.isBackendValid === false) {
    return resolveFieldErrorMessage(card.backendValidationResult, t as any);
  }
  return '';
}

function onCheckoutAddCardCancel() {
  addCardDialogOpen.value = false;
}

async function onCheckoutAddCardSaveClick() {
  const form = (vm.$refs as any).checkoutLinkForm;
  if (form && typeof form.submitFromParent === 'function') {
    await form.submitFromParent();
  }
}

async function onSubmitCheckoutAddCard({
  cardNumber, phone, firstName, lastName,
}: any) {
  store.commit('common/setGlobalLoading', true);
  try {
    await withNightCardNameFallback({
      firstName,
      lastName,
      request: ({ firstName: dtoFirstName, lastName: dtoLastName }: any) => SUserService.updateProfile({
        nightRiderCardDetailsFormDTO: {
          cardNumber,
          phone,
          firstName: dtoFirstName,
          lastName: dtoLastName,
        },
      }),
    });
    addCardDialogOpen.value = false;
    emit('night-card-linked');
  } catch (e: any) {
    store.commit('common/setGlobalLoading', false);
    if (e?.status === 400) {
      emitNightCardLinkError(vm, e);
    } else if (e?.status !== 401) {
      await setGlobalError(e, false);
    }
  }
}

async function checkCards() {
  if (checkoutNoLinkedCardBlock.value) {
    emit('updateNightCards', []);
    return;
  }

  pruneStaleCardValidationResults();

  const cardNumbers = collectCardsForValidation();
  const hasLinkedCardToValidate = !props.isAnotherPassenger && !!effectiveLinkedCardNumber.value;

  if (!shouldRunNightCardPriceCheck()) {
    emit('updateNightCards', []);
    return;
  }

  const isFormValid = await validateFormForPriceCheck();
  if (!isFormValid && !hasLinkedCardToValidate) {
    return;
  }

  const bookingRequestNightCardsModel = {
    bookingRequestId: getBookingRequestId.value,
    adultsCount: getAdults.value,
    infantsCount: getChildren.value,
    nightRiderCards: cardNumbers,
    ...(props.isAnotherPassenger && getPassengerPhone.value
      ? { passengerPhoneNumber: getPassengerPhone.value }
      : {}),
  };

  const { data } = await bookingRequestNightCardsCheck(bookingRequestNightCardsModel);
  const cardsFromLocalStorage = JSON.parse(localStorage.getItem('cards') as string);
  const cardsSavedInLocalStorage = cardsFromLocalStorage ? [...cardsFromLocalStorage] : [];

  getNightRiderCardValidationEntries(data).forEach(([key, value]) => {
    const parsedResult = parseNightCardValidationResult(value);
    applyCardValidationResult(key, value);

    if (props.isAnotherPassenger) {
      return;
    }

    const cardIndex = cards.value.findIndex(({ value: v }) => key === v);
    if (cardIndex < 0) {
      return;
    }

    if (isNightCardDiscountEligible(parsedResult)) {
      const existingCard = cardsSavedInLocalStorage.find(({ value: v }: any) => v === cards.value[cardIndex].value);
      cards.value.splice(cardIndex, 1, {
        ...cards.value[cardIndex],
        isBackendValid: true,
        backendValidationResult: parsedResult,
      });
      if (!existingCard) {
        cardsSavedInLocalStorage.push({
          id: cards.value[cardIndex].id,
          value: cards.value[cardIndex].value,
          isFrontValid: true,
          isBackendValid: true,
        });
      }
    }
  });

  if (cardsSavedInLocalStorage.length) {
    localStorage.setItem('cards', JSON.stringify(cardsSavedInLocalStorage));
  }

  syncLinkedCardValidationResult();

  const newPrice = {
    originalOutwardFeaturePrice: data.originalOutwardFeaturePrice,
    originalOutwardPrice: data.originalOutwardPrice,
    originalReturnFeaturePrice: data.originalReturnFeaturePrice,
    originalReturnPrice: data.originalReturnPrice,
  };

  const validCardsForOrder = getValidCardsForOrder();

  emit('updateNightCards', validCardsForOrder);
  store.commit('booking/setUpdatedPriceByNightCards', newPrice);
  rebuildCardsFromAdults();
}

function onNightCardContextChanged() {
  rebuildCardsFromAdults();
  restoreFromLocalStorage();
  checkCards();
}

watch(() => props.isAnotherPassenger, (isSelected) => {
  if (!isSelected) {
    Object.keys(cardValidationResults).forEach((key) => {
      Vue.delete(cardValidationResults, key);
    });
    linkedCardValidationResult.value = null;
  }
  onNightCardContextChanged();
});

watch(isMainPassengerDiscountEligible, (isEligible) => {
  if (props.isAnotherPassenger || showLinkedAccountCardField.value) {
    rebuildCardsFromAdults();
    if (!isEligible) {
      checkCards();
    }
  }
});

watch(() => props.linkedNightRiderCardNumber, () => {
  onNightCardContextChanged();
});

watch(getAdults, () => {
  rebuildCardsFromAdults();
  checkCards();
});

watch(getPassengerPhone, () => {
  if (collectCardsForValidation().length > 0 || props.isAnotherPassenger) {
    checkCards();
  }
});

watch(getSelectedRides, () => {
  if (shouldRunNightCardPriceCheck()) {
    checkCards();
  }
}, { deep: true });

watch(getAppConfig, (cfg) => {
  if (cfg?.isNightCardsSupport) {
    onNightCardContextChanged();
  }
}, { deep: true });

watch(addCardDialogOpen, (isOpen) => {
  if (!isOpen) {
    nextTick(() => {
      const f = (vm.$refs as any).checkoutLinkForm;
      if (f && typeof f.reset === 'function') {
        f.reset();
      }
    });
  }
});

onNightCardContextChanged();

defineExpose({
  onMainPassengerNightCardInput,
  onMainPassengerNightCardBlur,
  getAdditionalCardsForDuplicateCheck,
  getMainPassengerValidationResult,
  validateForm,
});
</script>

<style lang="scss" scoped>
.night-card-add-dialog-actions {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  box-sizing: border-box;
}

.left-block {
  max-width: 200px;
}

@media (max-width: 768px) {
  .left-block {
    max-width: 100%;
  }
}

.night-card-add-dialog-actions__cell {
  flex: 1 1 0;
  min-width: 0;
}
</style>
